import { useEffect, useState } from "react";
import type { MemoryDTO } from "../../shared/types";
import { del, get, patch, post } from "../lib/api";
import { changedBy } from "../lib/memory";
import { Icon } from "./Icon";
import { List, listRowCls, ListRow } from "./List";
import { confirmDialog } from "./Dialog";
import { Button, ErrorText, Field, inputCls, timeAgo } from "./ui";
import { Switch } from "./Switch";

const WRITER: Record<MemoryDTO["source"], string> = { agent: "written by an agent", admin: "added by an admin", user: "added by a person", system: "added by the platform" };

/** Who wrote a memory: the agent whose run saved it, else the kind of writer; then who last changed it, when that is news. */
const authorOf = (m: MemoryDTO) => `${m.authorAgentSlug ? `written by ${m.authorAgentSlug}` : WRITER[m.source]}${changedBy(m) ? ` · changed by ${changedBy(m)}` : ""}`;

/** A scope's own memory: what every agent working under it knows about it. Pinned notes are in every run. */
export function ScopeMemory({ subjectId }: { subjectId: string }) {
  const [memories, setMemories] = useState<MemoryDTO[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [pinned, setPinned] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = () => get<MemoryDTO[]>(`/api/memories?subjectId=${subjectId}&limit=500`).then(setMemories, (e) => setError(e.message));
  useEffect(() => {
    void load();
  }, [subjectId]);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const sorted = [...(memories ?? [])].sort((a, b) => Number(b.pinned) - Number(a.pinned) || Date.parse(b.updatedAt) - Date.parse(a.updatedAt));

  return (
    <List
      title={memories ? `Memory · ${memories.length}` : "Memory"}
      hint="What every agent working under it knows. Pinned notes are in every run; the others are listed by title for agents to look up."
      action={!adding && <Button onClick={() => setAdding(true)}>Add a note</Button>}
      empty={memories?.length === 0 ? "Nothing remembered yet. Agents save what they learn here; add a note for them, such as who decides or what to avoid." : undefined}
      footer={error && <ErrorText error={error} />}
    >
      {adding && (
        <div className={`${listRowCls} space-y-3`}>
          <Field label="Title">
            <input className={inputCls} value={title} placeholder="Who decides" onChange={(e) => setTitle(e.target.value)} autoFocus />
          </Field>
          <Field label="Note">
            <textarea className={inputCls} rows={4} value={content} onChange={(e) => setContent(e.target.value)} />
          </Field>
          <div className="flex flex-wrap items-center gap-2">
            <Switch className="mr-auto" label="Pin, so every run has it" checked={pinned} onChange={setPinned} />
            <Button onClick={() => setAdding(false)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={busy || !title.trim() || !content.trim()}
              onClick={() =>
                run(async () => {
                  await post("/api/memories", { level: "scope", subjectId, title: title.trim(), content, pinned, tags: [] });
                  setTitle("");
                  setContent("");
                  setPinned(false);
                  setAdding(false);
                })
              }
            >
              Save note
            </Button>
          </div>
        </div>
      )}
      {sorted.map((m) => (
        <ListRow
          key={m.id}
          title={
            <>
              {m.pinned && <Icon name="pin" className="size-3.5 shrink-0 text-muted" />}
              <span className="min-w-0 truncate font-medium">{m.title}</span>
            </>
          }
          trailing={
            <>
              <Button variant="ghost" size="sm" className="shrink-0" disabled={busy} onClick={() => run(() => patch(`/api/memories/${m.id}`, { pinned: !m.pinned }))}>
                {m.pinned ? "Unpin" : "Pin"}
              </Button>
              <Button variant="danger" size="sm" className="shrink-0" disabled={busy} onClick={async () => (await confirmDialog({ title: `Delete "${m.title}"?`, body: "The memory is gone for good.", confirm: "Delete memory", danger: true })) && run(() => del(`/api/memories/${m.id}`))}>
                Delete
              </Button>
            </>
          }
        >
          <p className="mt-1.5 line-clamp-6 text-sm whitespace-pre-wrap text-muted">{m.content}</p>
          <div className="mt-2 text-xs text-faint">
            {m.pinned ? "Pinned scope memory" : "Scope memory"} · {authorOf(m)} · updated {timeAgo(m.updatedAt)}
          </div>
        </ListRow>
      ))}
    </List>
  );
}
